import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    CounterBadge,
    Field,
    InfoLabel,
    Input,
    makeStyles,
    Text,
} from '@fluentui/react-components'
import { LineItemGroupDialogProps } from './LineItemGroupDialog'
import RatePanel from './RatePanel'
import LineItemPanel from './LineItemPanel'
import { Stack } from '@fluentui/react'
import { useFormikContext } from 'formik'
import { AccountingId, ConsolidationType, InvoiceMutationDto, LineItemGroupType } from '../../../../../../config/finance/invoiceConfig'
import SingleUnitDimensionFields from '../../../../../../v2/components/Form/Accounting/SingleUnitDimensionFields'
import { DimensionsDto } from '../../../../../../v2/components/Form/Accounting/DimensionFields'
import BaseCombobox from '../../../../../Combobox/BaseCombobox'
import EnumCombobox from '../../../../../Combobox/EnumCombobox'
import FormContainer from '../../../../FormContainer'
import { useTranslation } from 'react-i18next'
import { PricePrecedence } from '../../../../../../config/finance/financeConfig'
import FileCombobox from '../../../../../Combobox/FileCombobox'
import useVatTypes from '../../../../../../hooks/useVatTypes'
import { PriceAmountCallback } from '../../../../../../helpers/priceHelper'
import { formatDecimals, parseNumber } from '../../../../../../helpers/numberHelper'

const useStyles = makeStyles({
    accordion: {
        '& .fui-AccordionHeader__button, & .fui-AccordionPanel': {
            marginLeft: 0,
            marginRight: 0,
            paddingLeft: 0,
            paddingRight: 0,
        },
    },
})

export function Body(props: LineItemGroupDialogProps): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const { values } = useFormikContext<InvoiceMutationDto>()
    const fileRef = useRef<HTMLInputElement>()
    const descriptionRef = useRef<HTMLInputElement>()
    const { getVatType } = useVatTypes()

    useEffect(() => {
        if (fileRef.current) {
            fileRef.current?.focus()
        } else {
            descriptionRef.current?.focus()
        }
    }, [descriptionRef.current])

    const template = useMemo(() => values.templateId && props.templates?.find(template => template.id === values.templateId), [values.templateId])
    const templateGroup = useMemo(
        () => props.values.templateGroupId && template?.lineItemGroups?.find(group => group.id === props.values.templateGroupId),
        [props.values.templateGroupId],
    )

    useEffect(() => {
        if (templateGroup) {
            props.updateValues({
                accounting: templateGroup.accounting,
                consolidation: templateGroup.consolidation,
                showStatement: templateGroup.showStatement,
            })
        }
    }, [templateGroup])

    //Apply the chosen Fixed Price.
    useEffect(() => {
        if (props.values.fileId) {
            props.updateValues({
                file: props.meta.data?.files
                    ?.map(file => ({ ...file.file, ownerId: file.file.owner.id }))
                    .find(file => file.id === props.values.fileId),
            })
        }
    }, [props.values.fileId])

    //Apply the chosen Fixed Price.
    useEffect(() => {
        if (props.values.fixedPriceId) {
            const fixedPrice = props.meta.data?.files
                .find(file => file.file?.id === props.values.fileId)
                ?.fixedPrices.find(fixedPrice => fixedPrice.id === props.values.fixedPriceId)
            const precedence = fixedPrice
                ? fixedPrice.type?.includingVat
                    ? PricePrecedence.Total
                    : PricePrecedence.Subtotal
                : PricePrecedence.Subtotal

            props.updateValues({
                accounting: fixedPrice?.type?.accounting ?? props.values.accounting,
                description: fixedPrice?.description ?? props.values.description,
                precedence: precedence,
                price:
                    fixedPrice.type?.includingVat || false
                        ? formatDecimals(
                              PriceAmountCallback.get(PricePrecedence.Subtotal)(
                                  parseNumber(fixedPrice.rate?.amount, 'en'),
                                  getVatType(fixedPrice.vatType),
                              ),
                              8,
                              'en',
                              'en',
                              false,
                          )
                        : fixedPrice.rate?.amount,
                showStatement: fixedPrice?.type?.showStatement ?? props.values.showStatement,
            })
        }
    }, [props.values.fixedPriceId])

    return (
        <Accordion multiple collapsible defaultOpenItems={['general', 'rate', 'line-items']} className={styles.accordion}>
            <AccordionItem value="general">
                <AccordionHeader>{translate('invoice.edit.form.invoicegroup.general.label')}</AccordionHeader>
                <AccordionPanel>
                    <FormContainer>
                        {props.hasMultipleFiles && props.values.fileId && (
                            <Field label={translate('invoice.edit.form.invoicegroup.general.fileid')} required>
                                <FileCombobox
                                    required
                                    componentRef={fileRef}
                                    selectedOptions={[props.values.fileId]}
                                    onOptionSelect={(_, data) => props.updateValues({ fileId: data.selectedOptions?.[0] })}
                                    filter={file => values.fileIds?.includes(file.id)}
                                />
                            </Field>
                        )}

                        <Field label={translate('invoice.edit.form.invoicegroup.general.description')} required>
                            <Input
                                required
                                ref={descriptionRef}
                                onFocus={event => event.target.select()}
                                value={props.values.description}
                                placeholder={translate('base.table.header.description')}
                                onChange={(_, { value }) => props.updateValues({ description: value })}
                            />
                        </Field>
                        {values.unit && props.values.isNew && (
                            <Field
                                label={{
                                    children: (
                                        <InfoLabel info={translate('invoice.edit.form.invoicegroup.general.template.tooltip')}>
                                            {translate('invoice.edit.form.invoicegroup.general.template')}
                                        </InfoLabel>
                                    ),
                                }}
                            >
                                <BaseCombobox<LineItemGroupType>
                                    entities={template?.lineItemGroups}
                                    selectedOptions={[props.values.templateGroupId]}
                                    getEntityValue={entity => entity.id}
                                    getEntityLabel={entity => entity.description}
                                    onOptionSelect={(_, data) => {
                                        props.updateValues({ templateGroupId: data.selectedOptions?.[0] })
                                    }}
                                />
                            </Field>
                        )}
                    </FormContainer>
                </AccordionPanel>
            </AccordionItem>
            <AccordionItem value="rate">
                <AccordionHeader>{translate('invoice.edit.form.invoicegroup.rate.label')}</AccordionHeader>
                <RatePanel {...props} />
            </AccordionItem>
            <AccordionItem value="line-items">
                <AccordionHeader>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 's1' }}>
                        <Text>{translate('invoice.edit.form.invoicegroup.lineitems')}</Text>
                        <CounterBadge size="small" count={props.values.lineItems.length} showZero />
                    </Stack>
                </AccordionHeader>
                <LineItemPanel {...props} />
            </AccordionItem>
            <AccordionItem value="accounting">
                <AccordionHeader>{translate('invoice.edit.form.invoicegroup.accounting')}</AccordionHeader>
                <AccordionPanel>
                    <SingleUnitDimensionFields
                        values={props.values.accounting}
                        setValue={(property: keyof DimensionsDto, item: AccountingId) =>
                            props.updateValues({
                                accounting: {
                                    ...props.values.accounting,
                                    [property]: item,
                                },
                            })
                        }
                        unitKey={values.unit}
                    />
                    <Field label="Clustering">
                        <EnumCombobox<ConsolidationType>
                            entities={[ConsolidationType.NONE, ConsolidationType.DIMENSIONS, ConsolidationType.PERIOD, ConsolidationType.BOTH]}
                            labelKeyPrefix={'base.consolidationtype'}
                            selectedOptions={[props.values.consolidation]}
                            onOptionSelect={(_, data) => props.updateValues({ consolidation: data.selectedOptions?.[0] as ConsolidationType })}
                        />
                    </Field>
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    )
}
